import { MouseEvent } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { format } from "date-fns";
import { Tooltip } from "react-tooltip";

import { TransactionModel } from "@interfaces/index";
import { formatAmount, getUploadLaterTooltipContentStr } from "@utils/index";
import {
  PencilSvg,
  TrashSvg,
  TransactionStatus,
  AlertTriangleSvg,
  InfoSquareRoundedSvg,
} from "@components/index";

export const TableTdDesktopTransaction = ({
  transaction,
  hideAssetColumn,
  onRemove,
  onClickRow,
}: {
  transaction: TransactionModel;
  hideAssetColumn: boolean;
  onRemove?: (id: string) => void;
  onClickRow?: () => void;
}) => {
  const handleRemove = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove(transaction.uniqueId);
    }
  };

  const files = transaction.attachments?.length || 0;

  return (
    <tr
      className="group cursor-pointer border-b border-b-black-8 last:border-b-0 hover:bg-black-4"
      onClick={onClickRow}
      data-testid="tr_transactionTable"
    >
      <td className="py-3 text-black-54">
        {format(new Date(transaction.date), "dd.MM.yyyy")}
      </td>
      <td className="py-3 text-black-100">
        {transaction.isImported ? "Imported" : "FBTC"}
      </td>
      <td className="hidden py-3 font-bold capitalize text-black-100 lg:table-cell">
        {transaction.type}
        {transaction.refund && (
          <>
            <span
              className="block text-100 font-normal text-black-54 xxl:inline"
              data-tooltip-id={`tooltip-refund-${transaction.uniqueId}`}
              data-tooltip-content={
                transaction.refundOf?.uniqueId
                  ? `Refund of Transaction #${transaction.refundOf?.uniqueId}`
                  : "Refund"
              }
              data-tooltip-variant="dark"
            >
              <span className="hidden xxl:inline"> • </span>
              Refund
            </span>
            <Tooltip id={`tooltip-refund-${transaction.uniqueId}`} />
          </>
        )}
      </td>
      <td className="py-3">
        {transaction.isImported && (
          <div
            className="flex w-fit rounded-md bg-green-100 px-2 text-white-100"
            title="Imported transaction category in external system"
          >
            {transaction.externalCategoryTitle || "Unmapped Category"}
          </div>
        )}
        <div className="text-black-100">{transaction.category.title}</div>
        <div className="text-black-54">{transaction.subcategory.title}</div>
      </td>
      {!hideAssetColumn && (
        <td className="py-3 text-black-100">
          {transaction.asset?.name && (
            <>
              <div
                className="flex w-fit items-center"
                data-tooltip-id={`tooltip-upload-later-${transaction.uniqueId}`}
                data-tooltip-variant="light"
                data-tooltip-class-name="!rounded-lg shadow-md !p-4 !text-100"
                data-tooltip-html={getUploadLaterTooltipContentStr(
                  transaction.asset,
                )}
              >
                {transaction.asset.uploadLaterAgreement ||
                transaction.asset.uploadLaterInvoice ? (
                  <div className="mr-0.5 hidden md:block">
                    <AlertTriangleSvg />
                  </div>
                ) : null}
                <div className="max-w-24 truncate">
                  {transaction.asset.name}
                </div>
              </div>
              {transaction.asset.uploadLaterAgreement ||
              transaction.asset.uploadLaterInvoice ? (
                <>
                  <Tooltip
                    id={`tooltip-upload-later-${transaction.uniqueId}`}
                    className="hidden !opacity-100 md:block"
                  />
                  <div className="mt-1 flex items-center text-100 text-black-70 *:mr-1 *:size-4 md:hidden">
                    <AlertTriangleSvg />
                    Documents required
                  </div>
                </>
              ) : null}
            </>
          )}
        </td>
      )}
      <td className="py-3 text-black-100">
        <div className="flex items-center gap-x-2 capitalize">
          <div className="h-3 w-3 shrink-0 rounded-full border-2 border-primary-300" />
          {transaction.period.title}
        </div>
      </td>
      <td className="hidden pb-4 pt-5 text-black-54 xl:table-cell">
        {!files ? (
          "No"
        ) : (
          <div
            className={clsx(
              files &&
                "underline decoration-black-54 hover:text-black-100 hover:decoration-black-100",
            )}
          >
            {files} file{files > 1 ? "s" : ""}
          </div>
        )}
      </td>
      <td className="py-3 text-button">
        <TransactionStatus status={transaction.status} />
      </td>
      <td className="hidden py-3 text-black-54 lg:table-cell">
        <div
          className="w-32 truncate xl:w-44 xxl:w-72"
          title={transaction.description}
        >
          {transaction.description}
        </div>
      </td>
      <td className="py-3 pr-6">
        <div className="flex items-center justify-end gap-x-1">
          <span
            className={clsx(
              "font-bold",
              transaction.type === "income"
                ? "text-green-100"
                : "text-black-100",
            )}
          >
            {formatAmount(transaction.amount)}
          </span>
          {transaction.disallowed && (
            <>
              <div
                className="relative cursor-pointer text-black-54"
                data-tooltip-id={`tooltip-disallowed-${transaction.uniqueId}`}
                data-tooltip-content="This is a disallowed transaction and will not be included in your calculations."
                data-tooltip-variant="light"
              >
                <InfoSquareRoundedSvg />
              </div>
              <Tooltip id={`tooltip-disallowed-${transaction.uniqueId}`} />
            </>
          )}
        </div>
      </td>
      <td className="pr-4">
        <div className="hidden justify-end gap-x-2 text-black-54 group-hover:flex">
          {transaction.policies.canUpdate && (
            <Link
              to={`/transactions/update/${transaction.uniqueId}`}
              onClick={(e) => e.stopPropagation()}
            >
              <PencilSvg />
            </Link>
          )}
          {transaction.policies.canUpdate && transaction.policies.canDelete && (
            <div className="h-6 w-px bg-black-16" />
          )}
          {transaction.policies.canDelete && (
            <button type="button" onClick={handleRemove}>
              <TrashSvg />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};
